import { useContext } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorTypes, FeatureFlags } from '@fabric-ui/components';
import { envVariables } from '@fabric-ui/utils';
import { ClientError, RequestDocument } from 'graphql-request';
import { print } from 'graphql/language/printer';
import { useAuth } from '../components/amplifyProvider';
import { GraphQLClientContext } from '../context/GraphQLClientProvider';
import { useNetworkStatus } from '../context/NetworkStatusContext';
import useHandleError, { serializeError } from '../hooks/useHandleError';
import { APP_PATHS } from '../types';
import { getAccountTokenFromLS } from './auth.utils';
import { useNavigate } from './AvRouter';
import { coerceVariablesWithTypes, extractVariableTypes, getGraphQLError, getOperationName, safeDocumentParse } from './graphqlUtils';
import { AuthStorageKeys } from './localStorageAuthUtils.utils';
import { getAppIdFromPathName, getPathNameByAccount } from './router.utils';
import { getNetworkErrorCode } from './utils';

type RequestOptions = {
  options?: any;
  body?: any;
  isJson?: boolean;
  onError?: (errors: any) => void;
  isWebserver?: boolean;
  muteErrors?: boolean;
  onSuccess?: (d: any) => any;
  signal?: AbortSignal;
};

type ApiFunction = (uri: string, { options, body, isJson, onError, isWebserver, muteErrors }?: RequestOptions) => Promise<any>;

export default function useApi(accountId, refetchAccessToken, featureFlags): ApiFunction {
  const navigate = useNavigate();
  const { client } = useContext(GraphQLClientContext);
  const { isOnline } = useNetworkStatus();
  const handleError = useHandleError(featureFlags[FeatureFlags.ShowServerErrorInToast]);
  const checkUnauthorized = status => status === 401;
  const checkPermission = status => status === 418;
  const { logout } = useAuth();

  const navigateNotFound = () => {
    const accountIdLocalStorage = localStorage.getItem(AuthStorageKeys.accountId);
    navigate(
      accountIdLocalStorage
        ? getPathNameByAccount(accountIdLocalStorage, getAppIdFromPathName(window.location.pathname) || APP_PATHS.PLATFORM)('not-found')
        : { to: 'not-found' }
    );
  };

  return async (
    uri: RequestDocument,
    { options = {}, body, isJson = true, onError, onSuccess = d => d, isWebserver = true, muteErrors, signal }: RequestOptions = {}
  ) => {
    const graphqlErrorHandling = async (error: ClientError, isRetry: boolean | undefined, additionalInfo: any) => {
      if (checkUnauthorized(getNetworkErrorCode(error))) {
        const accessToken = getAccountTokenFromLS(accountId);
        if (isRetry) {
          console.error(
            `${ErrorTypes.Authentication}: Error fetching new access token after retry, logging out: ${!accessToken}, graphqlName: ${
              additionalInfo.graphqlName
            }, account: ${accountId}`
          );

          if (!accessToken) {
            datadogRum.addAction(`logout was triggered from useApi - graphql error, no access token after retry`, { accountId });
          }

          await logout(!!accessToken);
          return undefined;
        }
        return refetchAccessToken(accountId)
          .then(res => fetchGraphql(res, true))
          .catch(async () => {
            console.error(
              `${ErrorTypes.Authentication}: Error fetching new access token, logging out: ${!accessToken}, graphqlName: ${
                additionalInfo.graphqlName
              }, account: ${accountId}`
            );

            if (!accessToken) {
              datadogRum.addAction(`logout was triggered from useApi - rest error, no access token before retry`, { accountId });
            }

            await logout(!!accessToken);
          });
      }
      if (checkPermission(getNetworkErrorCode(error))) {
        navigateNotFound();
        return undefined;
      }

      if (!isOnline || !navigator.onLine) {
        console.warn({
          status: ErrorTypes.Offline,
          error: serializeError(error),
        });
        return undefined;
      }

      const gqlError = getGraphQLError(error) || error;
      if (muteErrors) {
        throw gqlError;
      } else if (onError && !signal?.aborted) {
        onError(gqlError);
      } else {
        handleError(gqlError, additionalInfo);
      }
      throw error;
    };

    const restErrorHandling = async (res, isRetry) => {
      if (!(res instanceof Response)) {
        const error = res?.message || res;
        if (muteErrors) {
          throw error;
        } else if (onError) {
          onError(error);
        } else {
          handleError(error);
        }
        return undefined;
      }
      const isTextError = res.headers?.get('content-type')?.startsWith('text/');
      const responseContent = await (isTextError ? res.text() : res.json()).catch(() => res.statusText || 'Unknown error');
      datadogRum.addAction(`Error:REST:${uri}`, { request: { uri, options }, response: responseContent, hasError: true });
      const error = responseContent;
      if (checkUnauthorized(getNetworkErrorCode(res))) {
        const accessToken = getAccountTokenFromLS(accountId);
        if (isRetry) {
          if (!accessToken) {
            datadogRum.addAction(`logout was triggered from useApi - rest error, no access token after retry`, { accountId });
          }
          await logout(!!accessToken);
          return undefined;
        }
        return refetchAccessToken(accountId)
          .then(res => fetchRest(res, true))
          .catch(async () => {
            if (!accessToken) {
              datadogRum.addAction(`logout was triggered from useApi - rest error, no access token before retry`, { accountId });
            }
            await logout(!!accessToken);
          });
      }
      if (checkPermission(getNetworkErrorCode(error))) {
        navigateNotFound();
        return undefined;
      }
      if (muteErrors) {
        throw error;
      } else if (onError) {
        onError(error);
      } else {
        handleError(error);
      }
      throw error;
    };

    const fetchGraphql = async <TData,>(accessToken: string, isRetry?: boolean): Promise<TData | null> => {
      const document = safeDocumentParse(uri);
      if (!document) {
        return null;
      }

      const graphqlName = getOperationName(document);

      const variables = coerceVariablesWithTypes(options, extractVariableTypes(document));

      return client
        .request({
          document,
          variables,
          signal,
          requestHeaders: {
            ...options.headers,
            ...(shouldSendAccessToken() ? { Authorization: `Bearer ${accessToken}` } : {}),
            graphqlName,
            'x-full-url': window.location.pathname,
            Accept: 'application/json',
          },
        })
        .then((res: any) => {
          datadogRum.addAction(`Graphql:${graphqlName}`, {
            graphqlName,
            request: { uri: print(document), variables },
            response: res,
            hasError: Boolean(res.errors),
          });
          if (!muteErrors && res.errors) {
            if (onError) {
              onError(res.errors);
            } else {
              handleError(res.errors, { accountId, graphqlName });
            }
          }

          // TODO: Use `res` directly, this requires modifying all api result handling. JIRA: https://jira.corp.zscaler.com/browse/AVA-11474
          return onSuccess({ data: res });
        })
        .catch(error => {
          if (!globalThis.isReloading) {
            datadogRum.addAction(`Error:Graphql:${graphqlName}`, {
              graphqlName,
              request: { uri: print(document), variables },
              response: error,
            });
            return graphqlErrorHandling(error, isRetry, { accountId, graphqlName });
          }
          return undefined;
        });
    };

    const shouldSendAccessToken = () => envVariables.VITE_SEND_REQUESTS_WITH_TOKEN === 'true';
    const fetchRest = async (accessToken, isRetry?) => {
      const res = await fetch(isWebserver ? `${envVariables.VITE_WEBSERVER_API_URL}/${uri}` : `${envVariables.VITE_API_URL}/${uri}`, {
        body: isJson ? JSON.stringify(body) : body, // TODO fix ugliness
        ...options,
        headers: {
          ...(isJson ? { 'Content-Type': 'application/json' } : undefined),
          ...options.headers,
          ...(shouldSendAccessToken() ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      }).catch(res => res);
      if (res.ok) {
        const responseContent = await Promise.resolve(
          res.headers.get('Content-Type') === 'application/octet-stream' ? res : res.headers.get('content-length') === '0' ? '' : res.json()
        );
        datadogRum.addAction(`REST:${uri}`, { request: { uri, options }, response: responseContent, hasError: false });
        return onSuccess(responseContent) ?? responseContent;
      }
      return !globalThis.isReloading && restErrorHandling(res, isRetry);
    };

    if (safeDocumentParse(uri)) {
      return fetchGraphql(getAccountTokenFromLS(accountId));
    }

    return fetchRest(getAccountTokenFromLS(accountId));
  };
}
